import { CSSProperties, useEffect, useState } from 'react';
import {
    CheckboxFormFieldComponent,
    AdvancedSelectFormFieldComponent,
    FieldHeaderComponent,
    HintPopoverComponent,
    MultiSelectDropdownFormFieldComponent,
    TextFieldComponent
} from '@vivli/shared/components';
import {
    useActiveUserContext,
    useAssignedAppTypeContext,
    useOrganizationsApiContext,
    useVivliConfigContext
} from '@vivli/core/infrastructure/context';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { Size } from '@vivli/shared/theme';
import { useOrganizations } from '@vivli/features/organizations/infrastructure/hook';
import { IDropdownMenuItem } from '@vivli/shared/infrastructure/interface';
import { IOrganization } from '@vivli/features/organizations/infrastructure/interface';
import { first } from 'rxjs/operators';
import { useModalApiContext } from '@vivli/core/infrastructure/context';
import { LoadIndicatorCenteredView } from '@vivli/shared/views';

const orgPoliciesStyle = {
    display: 'flex'
};
const defaultApproverStyle: CSSProperties = {
    width: '50%',
    display: 'inline-block'
};
const checkboxStyle: CSSProperties = {
    width: '50%',
    display: 'inline-block'
};
const eachCheckboxStyle: CSSProperties = {
    width: '100%',
    display: 'flex',
    flexDirection: 'row'
};

const alphabeticalSort = (a, b) => {
    return a.title < b.title ? -1 : 1;
};

const vivliAdminCheckBoxes = [{ label: 'Data Contributor', name: 'isDataProvider', hint: null }];
const nonAmrCheckboxes = [
    {
        label: 'Default to Downloadable Study Data Packages',
        name: 'defaultToDownloadableStudyIPDDataPackages',
        hint:
            'If the data associated with studies from your organization are directly downloadable to a ' +
            "researcher's desktop, check this item.  If this item is not checked (the default) then the Data " +
            'Package comprising those data will only be made available in a highly protected and access controlled ' +
            'research environment.  Note that in both cases, the researcher will create a data request that will go ' +
            'through a review and approval process before the Data Package is made available.'
    },
    {
        label: 'Default to show Extended Study Metadata in user search screens.',
        name: 'defaultToShowExtendedMetadata',
        hint:
            'If you normally want users searching for your studies to see such details as Study Design, ' +
            'History, Outcomes, etc. check this item. If you normally do not want users to see these ' +
            'details, leave it unchecked. You can override to control Extended Metadata visibility for a ' +
            'particular study(ies)'
    },
    {
        label: "Default Requested Studies to 'Data loaded for this request only'.",
        name: 'defaultToThisRequestOnlyIpd',
        hint:
            'If you normally want to upload IPD data to each individual data request containing a study of yours, ' +
            'check this item - it allows you to customize data sets for each data request if you like.' +
            ' If you normally want to upload IPD data for the study once ' +
            'and allow the Vivli application to copy the stored data for any data request that needs it, ' +
            'leave it unchecked (this is the default behavior). Whichever setting you choose, you can ' +
            'override it later for any study in any data request.'
    },
    {
        label: 'Review Requests Pre-IRP/Reviewer Submission',
        name: 'reviewDataRequests',
        hint: null
    },
    { label: 'Accepts Enquiries', name: 'acceptEnquiry', hint: null }
];

const defaultVivliAdminCheckboxes = [
    { label: 'IRP/Reviewer', name: 'isIRPApprover', hint: null },
    { label: 'Study Meta Data Curator', name: 'isAnnotator', hint: null },
    { label: 'Study Curation QA Reviewer', name: 'isQaReviewer', hint: null }
];
const lastCheckBoxesNonAmr = [
    {
        label: 'Blinded To Data Requestor',
        name: 'blindReviewers',
        hint: null
    }
];

interface OrganizationPoliciesProps {
    showOrgPoliciesContent: boolean;
    organization: IOrganization;
}

export const OrganizationPolicies = ({ showOrgPoliciesContent, organization }: OrganizationPoliciesProps) => {
    if (organization) {
        organization.publicAccessTypes = organization?.publicAccessByDefaultFileTypes?.join(', ') ?? '';
    }
    const { user, hasPermission } = useActiveUserContext();
    const { isAmr } = useAssignedAppTypeContext();
    const { irpApproverOrgs } = useOrganizations();
    const config = useVivliConfigContext();
    const [publicAccessDefaultFileTypes, setPublicAccessDefaultFileTypes] = useState<object[]>([]);
    const [approverOptions, setApproverOptions] = useState<IDropdownMenuItem[]>();
    const isVivliAdmin = hasPermission(PermissionsEnum.CreateOrg);
    //TODO PERMFE - redo with permissions+
    const isReadOnly = !isVivliAdmin;
    const organizationsService = useOrganizationsApiContext();
    const modalService = useModalApiContext();

    let orgCheckBoxes: Array<{ label: string; name: string; hint: any }> = [];

    //order of checkboxes matters
    //TODO PERMFE - redo with permissions+
    if (isVivliAdmin) {
        orgCheckBoxes.push(...vivliAdminCheckBoxes);
    }
    if (showOrgPoliciesContent) {
        orgCheckBoxes.push(...nonAmrCheckboxes);
    }
    if (isVivliAdmin && !isAmr) {
        orgCheckBoxes.push(...defaultVivliAdminCheckboxes);
    }
    if (showOrgPoliciesContent) {
        orgCheckBoxes.push(...lastCheckBoxesNonAmr);
    }

    const emptyOption = { value: null, title: '' } as IDropdownMenuItem;

    const duaTemplateOptions = config?.duaTemplateItems
        .map(
            (dto) =>
                ({
                    title: dto.displayName,
                    value: dto.code,
                    valueKey: dto.displayName
                }) as IDropdownMenuItem
        )
        .sort(alphabeticalSort);

    const getApproverOptions = (data: IOrganization[]) => {
        return data
            ?.map(
                (org) =>
                    ({
                        title: org.name,
                        value: org.id
                    }) as IDropdownMenuItem
            )
            .sort(alphabeticalSort);
    };
    const handleError = (error: any) => {
        modalService.error(`An error occurred retrieving IRP approvers. Please try again or contact Vivli support. Error: ${error}`);
    };

    useEffect(() => {
        let shareables = config?.ipdFileTypes
            .filter((fileType) => fileType.publicAccessAllowable)
            .map((fileType) => ({ title: fileType.fileType }));

        if (config) {
            setPublicAccessDefaultFileTypes(shareables);
        }
    }, [config]);
    useEffect(() => {
        if (isReadOnly) {
            organizationsService
                .getIrpApproverOrganizations()
                .pipe(first((o) => o !== null))
                .subscribe((data) => {
                    setApproverOptions(getApproverOptions(data));
                }, handleError);
        } else {
            setApproverOptions(getApproverOptions(irpApproverOrgs));
        }
    }, [irpApproverOrgs]);

    return (
        <div>
            <FieldHeaderComponent title={'ORGANIZATION POLICIES'} />
            <div style={orgPoliciesStyle}>
                <div style={defaultApproverStyle}>
                    {!approverOptions ? (
                        <LoadIndicatorCenteredView />
                    ) : (
                        <div>
                            <AdvancedSelectFormFieldComponent
                                name={'irpApproverOrgId'}
                                label={'Default Approver Organization'}
                                items={[emptyOption, ...approverOptions]}
                                readonly={isReadOnly}
                            />
                        </div>
                    )}
                    <p />
                    {
                        //TODO PERMFE - redo with permissions - more below+
                        isVivliAdmin && duaTemplateOptions && (
                            <AdvancedSelectFormFieldComponent
                                name={'defaultDuaTemplateCode'}
                                label={'Default Dua Template'}
                                items={[emptyOption, ...duaTemplateOptions]}
                                readonly={isReadOnly}
                            />
                        )
                    }
                    <p />
                    <p />
                    When uploading data files, by default make the following type(s) publicly available on the "Study Documents" tab:
                    <p />
                    {isVivliAdmin && (
                        <MultiSelectDropdownFormFieldComponent
                            label={''}
                            name={'publicAccessByDefaultFileTypes'}
                            items={publicAccessDefaultFileTypes}
                            objectKey={'title'}
                            labelKey={'title'}
                            arrayKey={'title'}
                        />
                    )}
                    {!isVivliAdmin && (
                        <TextFieldComponent
                            defaultValue={organization.publicAccessTypes}
                            label={''}
                            readonly={isReadOnly}
                            //widthPerCharacter={5}
                        />
                    )}
                </div>
                <div style={{ width: 50 }} />

                <div style={checkboxStyle}>
                    {orgCheckBoxes.map(({ label, name, hint }, i) => (
                        <div key={i} style={eachCheckboxStyle}>
                            <CheckboxFormFieldComponent name={name} label={label} reverseOrientation={true} disabled={!isVivliAdmin} />
                            <HintPopoverComponent position={'top' as any} hintText={hint} maxWidth={Size.POPOVER_MAX_WIDTH} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
